// Modal focus-trap selector. Matches the focusable elements considered
// tabbable inside our dialogs: links, enabled form controls, and any node
// the author opted in via `tabindex` (excluding `tabindex="-1"`, which
// signals "programmatically focusable but not in the tab order").
const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

function focusableElementsIn(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
}

// Pure handler used by both the document-level keydown listener (FiltersSheet)
// and the React keydown handler (BarcodeScanner). Wraps focus from last to
// first when the user shift+tabs off the start, and from first to last on a
// forward tab off the end. Returns `true` when it preventedDefault, so the
// caller can return early.
//
// Caller is responsible for calling preventDefault — we only signal intent
// via the return value to keep this helper agnostic of whether the keydown
// was a native KeyboardEvent or a React.KeyboardEvent (their preventDefault
// shapes differ slightly across types).
export function trapTabKey(
  event: { key: string; shiftKey: boolean },
  root: HTMLElement | null,
): { focusTarget: HTMLElement; preventDefault: true } | null {
  if (event.key !== 'Tab' || !root) return null;
  const focusables = focusableElementsIn(root);
  if (focusables.length === 0) return null;
  const first = focusables[0]!;
  const last = focusables[focusables.length - 1]!;
  const active = document.activeElement as HTMLElement | null;
  if (event.shiftKey && active === first) return { focusTarget: last, preventDefault: true };
  if (!event.shiftKey && active === last) return { focusTarget: first, preventDefault: true };
  return null;
}

export { FOCUSABLE_SELECTOR, focusableElementsIn };
